import * as React from "react";

import { useHover } from "@/shared/hooks/useHover";
import { ErrorMonitoringService } from "@/shared/services";
import { LayoutItem, PlotPanelState } from "@/shared/state/visualization";
import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import { PlotPanelSettingsSidebarProps } from "../../PanelSettings/PlotPanelSettings";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import { useWorkspaceTimer } from "../../WorkspaceCtx";
import {
  useSelectPanel,
  useWorkspaceTimeBounds,
} from "../../WorkspaceCtx/hooks";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { NoDataMessage } from "../NoDataMessage";
import { PanelHeader } from "../PanelHeader";
import { PanelLayout } from "../PanelLayout";
import { RenderingError } from "../RenderingError";

import { Annotations } from "./Annotations";
import { ContextSensitiveButton } from "./ContextSensitiveButton/ContextSensitiveButton";
import * as axisConfigHooks from "./hooks/axisConfigHooks";
import * as loadingInfoHooks from "./hooks/loadingInfoHooks";
import * as plotHooks from "./hooks/plotManagerHooks";
import * as timerHooks from "./hooks/timerHooks";
import { usePaddedTimeBounds } from "./hooks/usePaddedTimeBounds";
import * as viewportHooks from "./hooks/viewportHooks";
import { Legend } from "./Legend";
import { PlotEvent, RenderedAnnotation } from "./messaging";
import { PlotLoadingState } from "./messaging/events";
import { PlotManager } from "./PlotManager";
import styles from "./PlotPanel.module.css";
import type { Extents } from "./PlotRenderer";
import { Toolbar } from "./plotTools";
import { SelectRegion } from "./SelectRegion";
import { SelectedRegionActionsMenu } from "./SelectRegion/SelectedRegionActionsMenu";
import { TimelineIndicator } from "./TimelineIndicator";
import { Tooltip } from "./Tooltip";
import { useViewport, Viewport } from "./Viewport";

interface PlotPanelProps {
  layout: LayoutItem;
  state: PlotPanelState;
}

export function PlotPanel({ layout, state }: PlotPanelProps) {
  const timer = useWorkspaceTimer();

  const selectPanel = useSelectPanel();

  const [canvasParentRef, isPanelHovered] = useHover<HTMLDivElement>();
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [chartExtents, setChartExtents] = React.useState<Extents | null>(null);
  const [isPlotLoading, setIsPlotLoading] =
    React.useState<PlotLoadingState>(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [plotManager, setPlotManager] = React.useState<PlotManager | null>(
    null,
  );
  const [annotations, setAnnotations] = React.useState<RenderedAnnotation[]>(
    [],
  );
  const [isDragging, setIsDragging] = React.useState(false);
  const [isZooming, setIsZooming] = React.useState(false);

  const localViewport = React.useMemo(
    () =>
      chartExtents
        ? new Viewport(chartExtents.data.normalizedViewport)
        : Viewport.default(0),
    [chartExtents],
  );

  const [sharedViewport] = useViewport();

  // Use initParams viewport if available, otherwise use shared viewport
  // This is only used for the initial viewport when the PlotManager first loads
  const initialViewportRef = React.useRef<Viewport>(
    state.initParams?.viewport
      ? new Viewport({
          x: state.initParams.viewport.x,
          y: state.initParams.viewport.y,
        })
      : sharedViewport,
  );
  const timeBounds = usePaddedTimeBounds(
    useWorkspaceTimeBounds({
      timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
    }),
  );
  const timeBoundsRef = React.useRef(timeBounds);
  timeBoundsRef.current = timeBounds;
  /**
   * Initialize the plot manager and clean up when the component is unmounted
   */
  React.useEffect(
    function init() {
      const div = canvasParentRef.current;
      if (div === null || error !== null) {
        return;
      }

      const abortController = new AbortController();

      const bounds = div.getBoundingClientRect();
      const canvas = document.createElement("canvas");
      canvasRef.current = canvas;
      canvas.width = bounds.width;
      canvas.height = bounds.height;
      canvas.classList.add(styles.canvas);
      div.insertBefore(canvas, div.firstChild);
      // Used to prevent the chart from updating its own extents
      // until after it has been able to apply the existing shared viewport.
      let hasAppliedSharedViewport = false;

      const manager = new PlotManager({
        canvas: canvas.transferControlToOffscreen(),
        devicePixelRatio: window.devicePixelRatio,
        timeBounds: timeBoundsRef.current,
      });

      manager.setEventListener(
        PlotEvent.LoadingStateChange,
        ({ isLoading }) => {
          if (abortController.signal.aborted) {
            return;
          }
          setIsPlotLoading(isLoading);
        },
      );

      manager.setEventListener(PlotEvent.Error, (err) => {
        if (abortController.signal.aborted || err.name === "AbortError") {
          return;
        }
        ErrorMonitoringService.captureError(err);
        setError(err);
        setIsPlotLoading(false);
        setPlotManager(null);
      });

      manager.setEventListener(
        PlotEvent.Rendered,
        ({ extents, annotations }) => {
          if (abortController.signal.aborted || !hasAppliedSharedViewport) {
            return;
          }
          setAnnotations(annotations);
          setChartExtents(extents);
        },
      );

      manager.setEventListener(PlotEvent.Initialized, () => {
        if (abortController.signal.aborted) {
          return;
        }
        // Update and apply the latest viewport down to the chart when it first loads.
        const refreshedViewport = new Viewport({
          ...initialViewportRef.current.getParams(),
          debounceId: undefined,
        });
        manager.applyViewport(refreshedViewport, "xy");
        hasAppliedSharedViewport = true;

        setPlotManager(manager);
      });

      return function dispose() {
        abortController.abort();
        manager.dispose();
        canvasRef.current = null;
        div.removeChild(canvas);
      };
    },
    [error, canvasParentRef],
  );

  axisConfigHooks.useDisableSyncOnLockedAxes(state.config);
  axisConfigHooks.useSaveAxisBounds(chartExtents, state.id, state.config);
  // TODO(ENG-1429): support multiple y axes, which will require not using one hook per axis.
  axisConfigHooks.useOnAxisConfigChanged(plotManager, state);

  viewportHooks.useOnSyncEnabled(localViewport);
  viewportHooks.useBroadcastLocalViewport(localViewport, state.id);
  viewportHooks.useApplySharedViewport(plotManager, isDragging);

  plotHooks.useZoom(plotManager, canvasRef, setIsZooming);
  plotHooks.usePan(plotManager, canvasRef, setIsDragging);
  plotHooks.useOnStateUpdated(plotManager, state);
  plotHooks.useOnThemeChanged(plotManager);
  const measured = plotHooks.useMeasureCanvas(plotManager);

  const showBackgroundLoading =
    loadingInfoHooks.useShowBackgroundLoading(isPlotLoading);

  timerHooks.useHoldWhileLoading(isPlotLoading);
  timerHooks.useSeekToClick(chartExtents, canvasRef, isDragging);

  const onDragChanged = React.useCallback(
    (_isDragging: boolean) => {
      setIsDragging(_isDragging);
    },
    [setIsDragging],
  );

  const sidebar = useWorkspaceSidebar();

  return (
    <PanelLayout
      header={
        <PanelHeader
          backgroundLoading={showBackgroundLoading}
          additionalTools={<Toolbar state={state} />}
          className={styles.panelHeader}
          state={state}
        />
      }
      isLoading={isPlotLoading === "foreground"}
      layout={layout}
      state={state}
    >
      <div
        className={styles.renderingSurface}
        ref={(node) => {
          if (node !== null) {
            measured(node);
          }
          canvasParentRef.current = node;
        }}
      >
        {error === null && (
          <TimelineIndicator chartExtents={chartExtents} timer={timer} />
        )}
        <Annotations chartExtents={chartExtents} annotations={annotations} />
        {error === null && <Legend state={state} />}
        {!layout.isResizing && (
          <Tooltip
            chartExtents={chartExtents}
            canvas={canvasRef.current}
            plotManager={plotManager}
            state={state}
          />
        )}
        <SelectRegion
          chartExtents={chartExtents}
          onDragChanged={onDragChanged}
          renderingSurface={canvasParentRef.current}
        />
        <ContextSensitiveButton
          isHostHovered={isPanelHovered}
          isHostIdle={!(isDragging || isZooming)}
          state={state}
          plotManager={plotManager}
          localViewport={localViewport}
          chartExtents={chartExtents}
        />
        <NoDataMessage
          panelData={state.data}
          onAddDataAction={() => {
            selectPanel(state.id);
            sidebar.openView<PlotPanelSettingsSidebarProps>({
              type: SidebarViewType.PanelSettings,
              data: {
                field: "Series",
                spotlightKey: performance.now(),
              },
            });
          }}
        />
        <DataNotFoundMessage panelId={state.id} />
        <RenderingError error={error} onClearError={() => setError(null)} />
      </div>
      <SelectedRegionActionsMenu
        panelId={state.id}
        plotManager={plotManager}
        chartExtents={chartExtents}
      />
    </PanelLayout>
  );
}
